import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { M2A } from 'www-cms';
import { BuildingBlocksModule } from '../building-blocks/building-blocks.module';
import { Meta } from '@angular/platform-browser';
import { WWW_CONFIG, WWWConfig } from '../shared/models/config.model';
import { QuestionAnswerSchemaDirective } from './question-answer-schema.directive';
import { home, lancaster, branson } from './google-structured-data';
import { Store } from '@ngxs/store';
import { PageSelectors } from './state/page.selectors';
import { SeoService } from 'common';
import { VisualEditingService } from 'directus';
import { ToolkitAssetListBlockComponent } from '../building-blocks/toolkit-asset-list-block/toolkit-asset-list-block.component';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'ss-www-page',
  templateUrl: './page.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BuildingBlocksModule,
    QuestionAnswerSchemaDirective,
    ToolkitAssetListBlockComponent,
  ],
})
export class PageComponent implements OnInit, AfterViewInit, OnDestroy {
  private seoService = inject(SeoService);
  private meta = inject(Meta);
  private wwwConfig = inject<WWWConfig>(WWW_CONFIG);
  private renderer = inject(Renderer2);
  private document = inject<Document>(DOCUMENT);
  private platformId = inject<Object>(PLATFORM_ID);
  private store = inject(Store);
  private visualEditingService = inject(VisualEditingService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  page = this.store.selectSignal(PageSelectors.page);
  buildingBlocks = computed(() =>
    this.page()?.buildingBlocks.filter((block) => !!block.item)
  );
  private removedMetaTags: HTMLMetaElement[] = [];
  isServerSide = isPlatformServer(this.platformId);

  ngOnInit(): void {
    const seo = this.page()!.seo;

    this.seoService.updateSeoTags(
      {
        ...seo,
        canonicalUrl: seo.canonicalUrl ?? undefined,
      },
      this.wwwConfig.baseAssetUrl
    );
    this.addGoogleStructuredData();
  }

  ngAfterViewInit(): void {
    this.visualEditingService.apply();
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.visualEditingService.apply());
  }

  ngOnDestroy(): void {
    this.removedMetaTags.forEach((tag) =>
      this.meta.updateTag({ name: tag.name, content: tag.content })
    );
  }

  trackBlock(index: number, block: M2A<any>) {
    return block.id;
  }

  private addGoogleStructuredData() {
    if (
      this.page()!.url !== '/' &&
      this.page()!.url !== '/lancaster' &&
      this.page()!.url !== '/branson'
    ) {
      const scripts = this.document.querySelectorAll('script');
      if (!scripts) return;
      const scriptsArray = Array.from(scripts);
      scriptsArray.forEach((script) => {
        if (script.id === 'structured-data') {
          this.renderer.removeChild(this.document.body, script);
        }
      });
      return;
    }
    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'structured-data';

    if (this.page()!.url === '/') {
      script.text = JSON.stringify(home);
    } else if (this.page()!.url === '/lancaster') {
      script.text = JSON.stringify(lancaster);
    } else if (this.page()!.url === '/branson') {
      script.text = JSON.stringify(branson);
    }

    this.renderer.appendChild(this.document.body, script);
  }
}
